<template>
    <aside v-if="open" id="global-ai-assistant" class="ai-drawer" aria-label="Assistente de IA">
        <header class="ai-drawer__header">
            <div class="ai-drawer__identity">
                <span class="ai-drawer__brand"><i class="bi-stars" aria-hidden="true"></i></span>
                <div>
                    <span class="ai-drawer__eyebrow">THALAMUS AI</span>
                    <strong>Assistente Global</strong>
                    <small>{{ contextLabel }}</small>
                </div>
            </div>
            <div class="ai-drawer__actions">
                <button type="button" :class="{ 'is-active': historyOpen }" :aria-pressed="historyOpen"
                    aria-label="Histórico de conversas" title="Histórico" @click="$emit('toggle-history')">
                    <i class="bi-clock-history" aria-hidden="true"></i>
                </button>
                <button type="button" aria-label="Nova conversa" title="Nova conversa" @click="$emit('new-conversation')">
                    <i class="bi-plus-lg" aria-hidden="true"></i>
                </button>
                <button class="ai-drawer__close" type="button" aria-label="Fechar assistente" title="Fechar" @click="$emit('close')">
                    <i class="bi-x-lg" aria-hidden="true"></i>
                </button>
            </div>
        </header>
        <div class="ai-drawer__body">
            <section v-if="historyOpen" class="ai-drawer__history-panel" aria-label="Histórico de conversas">
                <div class="ai-drawer__history-head">
                    <div>
                        <strong>Conversas</strong>
                        <small>Retome uma conversa ou comece outra.</small>
                    </div>
                    <button class="ai-drawer__history-close" type="button" aria-label="Fechar histórico"
                        title="Voltar ao chat" @click="$emit('toggle-history')">
                        <i class="bi-x-lg" aria-hidden="true"></i>
                    </button>
                </div>
                <div class="ai-drawer__history-tools">
                    <button class="ai-drawer__history-new" type="button" @click="$emit('new-conversation')">
                        <i class="bi-plus-lg" aria-hidden="true"></i>
                        Nova conversa
                    </button>
                    <form class="ai-drawer__history-search" @submit.prevent="$emit('search-history', historyQuery)">
                        <i class="bi-search" aria-hidden="true"></i>
                        <input v-model="historyQuery" type="search" placeholder="Buscar conversas" aria-label="Buscar conversas">
                        <button type="submit" aria-label="Buscar">Buscar</button>
                    </form>
                </div>
                <div class="ai-drawer__history-list">
                    <p v-if="conversationsLoading" class="ai-drawer__history-state">Carregando...</p>
                    <p v-else-if="!conversations.length" class="ai-drawer__history-state">Nenhuma conversa anterior.</p>
                    <template v-else>
                        <div
                            v-for="conversation in conversations"
                            :key="conversation.id"
                            class="ai-drawer__conversation"
                            :class="{ active: conversation.id === currentSessionId }"
                        >
                            <button type="button" class="ai-drawer__conversation-open"
                                @click="$emit('load-conversation', conversation.id)">
                                <span class="ai-drawer__conversation-icon"><i class="bi-chat-left-text" aria-hidden="true"></i></span>
                                <span class="ai-drawer__conversation-text">
                                    <strong>{{ conversation.title }}</strong>
                                    <small>{{ conversation.preview || formatActivity(conversation.last_activity_at) }}</small>
                                </span>
                            </button>
                            <span class="ai-drawer__conversation-actions">
                                <button type="button" aria-label="Renomear conversa" title="Renomear" @click="rename(conversation)">
                                    <i class="bi-pencil" aria-hidden="true"></i>
                                </button>
                                <button type="button" aria-label="Arquivar conversa" title="Arquivar"
                                    @click="$emit('archive-conversation', conversation.id)">
                                    <i class="bi-archive" aria-hidden="true"></i>
                                </button>
                            </span>
                        </div>
                    </template>
                </div>
            </section>
            <div v-if="showSkillIndicator" class="ai-drawer__mode">
                <span class="ai-drawer__mode-deep">
                    <i :class="skillStateIcon" aria-hidden="true"></i>
                    Skill ativa: {{ presentation.skillLabel }}
                    <b v-if="maturity && Number.isFinite(Number(maturity.score))">{{ maturity.score }}%</b>
                </span>
            </div>
            <AssistantFailureCard :failure="failure" :retrying="processing" @retry="$emit('retry')" />
            <p v-if="error" class="ai-drawer__error">{{ error }}</p>
            <AssistantChat :messages="messages" :queued-messages="queuedMessages" :processing="processing || polling"
                :locked="composerLocked" :activity="activity"
                :sources="sources" :placeholder="presentation.placeholder" @send="$emit('send', $event)" />
        </div>
    </aside>
</template>

<script>
import AssistantChat from './AssistantChat.vue'
import AssistantFailureCard from './AssistantFailureCard.vue'
import { shouldShowSkillIndicator } from '@/utils/ai-assistant.state'

export default {
    name: 'AssistantDrawer',
    components: {
        AssistantChat,
        AssistantFailureCard,
    },
    emits: [
        'close', 'send', 'retry', 'toggle-history', 'new-conversation',
        'load-conversation', 'rename-conversation', 'archive-conversation', 'search-history',
    ],
    props: {
        open: Boolean,
        context: { type: Object, default: null },
        messages: { type: Array, default: () => [] },
        queuedMessages: { type: Array, default: () => [] },
        processing: Boolean,
        polling: Boolean,
        sources: { type: Array, default: () => [] },
        error: { type: String, default: '' },
        skillState: { type: String, default: '' },
        maturity: { type: Object, default: null },
        activity: { type: Object, default: null },
        failure: { type: Object, default: null },
        composerLocked: Boolean,
        conversations: { type: Array, default: () => [] },
        conversationsLoading: Boolean,
        historyOpen: Boolean,
        currentTitle: { type: String, default: 'Nova conversa' },
        currentSessionId: { type: String, default: null },
        assistantState: { type: Object, default: () => ({ mode: 'chat', activeSkill: null }) },
        presentation: {
            type: Object,
            default: () => ({
                subtitle: 'Como posso ajudar?',
                placeholder: 'Pergunte ou peça algo ao Thalamus…',
                skillLabel: '',
            }),
        },
    },
    data() {
        return { historyQuery: '' }
    },
    computed: {
        showSkillIndicator() {
            return shouldShowSkillIndicator(this.assistantState) && Boolean(this.presentation.skillLabel)
        },
        contextLabel() {
            return this.presentation.subtitle
        },
        skillStateIcon() {
            if (this.skillState === 'clarifying') return 'bi-chat-dots-fill'
            if (this.skillState === 'ready') return 'bi-check-circle-fill'
            if (this.polling) return 'bi-arrow-repeat'
            return 'bi-shield-check'
        },
    },
    methods: {
        rename(conversation) {
            const title = window.prompt('Nome da conversa', conversation.title)
            if (title?.trim()) {
                this.$emit('rename-conversation', { id: conversation.id, title: title.trim() })
            }
        },
        formatActivity(value) {
            if (!value) return ''
            return new Date(value).toLocaleString('pt-BR')
        },
    },
}
</script>

<style scoped>
.ai-drawer {
    position: fixed;
    z-index: 1100;
    top: 0;
    right: 0;
    display: flex;
    width: min(31rem, 100vw);
    height: 100dvh;
    flex-direction: column;
    border-left: 1px solid rgba(208, 213, 221, 0.75);
    box-shadow: -22px 0 60px rgba(16, 24, 40, 0.18);
    background: linear-gradient(180deg, #f8faff 0%, #f9fafb 28%, #f6f7fb 100%);
    color: #101828;
}
.ai-drawer__header { display: flex; justify-content: space-between; gap: 1rem; align-items: center; padding: 1.15rem 1.1rem 1rem; border-bottom: 1px solid rgba(228, 231, 236, 0.8); background: rgba(255, 255, 255, 0.86); backdrop-filter: blur(16px); }
.ai-drawer__identity { display: flex; gap: 0.8rem; align-items: center; min-width: 0; }
.ai-drawer__identity > div { display: flex; min-width: 0; flex-direction: column; }
.ai-drawer__brand { display: grid; flex: 0 0 2.6rem; width: 2.6rem; height: 2.6rem; place-items: center; border-radius: 14px; background: linear-gradient(135deg, #4f46e5, #9333ea); color: #fff; box-shadow: 0 8px 18px rgba(99, 102, 241, 0.28); }
.ai-drawer__eyebrow { color: #6941c6; font-size: 0.62rem; font-weight: 800; letter-spacing: 0.12em; }
.ai-drawer__identity strong { margin-top: 0.08rem; font-size: 1.02rem; }
.ai-drawer__identity small { overflow: hidden; margin-top: 0.12rem; color: #667085; font-size: 0.76rem; text-overflow: ellipsis; white-space: nowrap; }
.ai-drawer__actions { display: flex; gap: 0.35rem; align-items: center; }
.ai-drawer__actions button { display: grid; width: 2.25rem; height: 2.25rem; padding: 0; place-items: center; border: 1px solid #eaecf0; border-radius: 50%; background: #fff; color: #475467; cursor: pointer; transition: background 0.2s, color 0.2s, border-color 0.2s; }
.ai-drawer__actions button:hover { background: #f2f4f7; }
.ai-drawer__actions button.is-active { border-color: #d9d6fe; background: #f4f3ff; color: #53389e; }
.ai-drawer__body { position: relative; display: flex; flex: 1; min-height: 0; flex-direction: column; }
.ai-drawer__close {
    display: grid;
    flex: 0 0 2.25rem;
    width: 2.25rem;
    min-width: 2.25rem;
    max-width: 2.25rem;
    height: 2.25rem;
    min-height: 2.25rem;
    padding: 0;
    place-items: center;
    border: 1px solid #eaecf0;
    border-radius: 50%;
    background: #fff;
    color: #475467;
    line-height: 1;
    cursor: pointer;
    font-size: 0.92rem;
    overflow: hidden;
    aspect-ratio: 1 / 1;
    transition: background 0.2s, transform 0.2s;
}
.ai-drawer__close:hover { background: #f2f4f7; transform: translateY(-1px); }
.ai-drawer__mode { display: flex; justify-content: space-between; gap: 0.5rem; padding: 0.55rem 1rem; border-bottom: 1px solid rgba(228, 231, 236, 0.8); background: rgba(255, 255, 255, 0.64); color: #667085; font-size: 0.7rem; font-weight: 600; }
.ai-drawer__mode span { display: inline-flex; gap: 0.3rem; align-items: center; }
.ai-drawer__mode b { padding: 0.08rem 0.32rem; border-radius: 99px; background: #eef2ff; font-size: 0.62rem; }
.ai-drawer__mode span:first-child { color: #3159b7; }
.ai-drawer__mode-deep { color: #6941c6 !important; }
.ai-drawer__error { margin: 0.65rem 1rem 0; padding: 0.65rem 0.8rem; border: 1px solid #fecdca; border-radius: 12px; color: #b42318; background: #fff4f3; font-size: 0.82rem; }
.ai-drawer__history-panel {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    background: #fff;
    animation: history-in 0.18s ease-out;
}
@keyframes history-in {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: none; }
}
.ai-drawer__history-head { display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; padding: 1rem 1.1rem 0.75rem; }
.ai-drawer__history-head > div { display: flex; min-width: 0; flex-direction: column; }
.ai-drawer__history-head strong { color: #101828; font-size: 1rem; }
.ai-drawer__history-head small { margin-top: 0.15rem; color: #667085; font-size: 0.78rem; }
.ai-drawer__history-close { display: grid; flex: 0 0 2rem; width: 2rem; height: 2rem; padding: 0; place-items: center; border: 1px solid #eaecf0; border-radius: 50%; background: #fff; color: #475467; cursor: pointer; font-size: 0.85rem; }
.ai-drawer__history-close:hover { background: #f2f4f7; }
.ai-drawer__history-tools { display: flex; flex-direction: column; gap: 0.6rem; padding: 0 1.1rem 0.85rem; border-bottom: 1px solid #eaecf0; }
.ai-drawer__history-new { display: inline-flex; justify-content: center; gap: 0.5rem; align-items: center; width: 100%; padding: 0.62rem 0.9rem; border: 0; border-radius: 10px; background: linear-gradient(135deg, #3159b7, #6941c6); color: #fff; box-shadow: 0 5px 12px rgba(79, 70, 229, 0.22); cursor: pointer; font-size: 0.88rem; font-weight: 700; transition: transform 0.2s, box-shadow 0.2s; }
.ai-drawer__history-new:hover { box-shadow: 0 7px 16px rgba(79, 70, 229, 0.3); transform: translateY(-1px); }
.ai-drawer__history-search { display: flex; gap: 0.5rem; align-items: center; padding: 0.15rem 0.2rem 0.15rem 0.7rem; border: 1px solid #d0d5dd; border-radius: 10px; background: #f9fafb; }
.ai-drawer__history-search:focus-within { border-color: #9e77ed; background: #fff; box-shadow: 0 0 0 3px rgba(158, 119, 237, 0.18); }
.ai-drawer__history-search i { color: #98a2b3; font-size: 0.85rem; }
.ai-drawer__history-search input { min-width: 0; flex: 1; padding: 0.45rem 0; border: 0; outline: 0; background: transparent; color: #101828; font-size: 0.88rem; }
.ai-drawer__history-search button { padding: 0.4rem 0.7rem; border: 1px solid #e4e7ec; border-radius: 8px; background: #fff; color: #53389e; cursor: pointer; font-size: 0.78rem; font-weight: 700; }
.ai-drawer__history-search button:hover { background: #f4f3ff; }
.ai-drawer__history-list { flex: 1; min-height: 0; overflow: auto; padding: 0.5rem 0.6rem 1rem; }
.ai-drawer__history-state { padding: 1.5rem 0.5rem; color: #667085; text-align: center; font-size: 0.88rem; }
.ai-drawer__conversation { display: flex; gap: 0.25rem; align-items: center; margin-bottom: 0.2rem; border: 1px solid transparent; border-radius: 12px; transition: background 0.15s, border-color 0.15s; }
.ai-drawer__conversation:hover { background: #f9fafb; border-color: #eaecf0; }
.ai-drawer__conversation.active { background: #f4f3ff; border-color: #d9d6fe; }
.ai-drawer__conversation-open { display: flex; flex: 1; min-width: 0; gap: 0.7rem; align-items: center; padding: 0.65rem 0.6rem; border: 0; border-radius: 12px; background: transparent; color: #101828; text-align: left; cursor: pointer; }
.ai-drawer__conversation-icon { display: grid; flex: 0 0 2rem; width: 2rem; height: 2rem; place-items: center; border-radius: 9px; background: #f2f4f7; color: #667085; font-size: 0.85rem; }
.ai-drawer__conversation.active .ai-drawer__conversation-icon { background: #e9d7fe; color: #53389e; }
.ai-drawer__conversation-text { display: flex; min-width: 0; flex-direction: column; }
.ai-drawer__conversation-text strong { overflow: hidden; color: #101828; font-size: 0.88rem; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.ai-drawer__conversation.active .ai-drawer__conversation-text strong { color: #53389e; }
.ai-drawer__conversation-text small { overflow: hidden; margin-top: 0.15rem; color: #667085; font-size: 0.76rem; text-overflow: ellipsis; white-space: nowrap; }
.ai-drawer__conversation-actions { display: flex; flex: 0 0 auto; gap: 0.15rem; padding-right: 0.35rem; opacity: 0; transition: opacity 0.15s; }
.ai-drawer__conversation:hover .ai-drawer__conversation-actions,
.ai-drawer__conversation.active .ai-drawer__conversation-actions,
.ai-drawer__conversation:focus-within .ai-drawer__conversation-actions { opacity: 1; }
.ai-drawer__conversation-actions button { display: grid; width: 1.85rem; height: 1.85rem; padding: 0; place-items: center; border: 0; border-radius: 8px; background: transparent; color: #667085; cursor: pointer; font-size: 0.85rem; }
.ai-drawer__conversation-actions button:hover { background: #fff; color: #53389e; box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12); }
@media (hover: none) {
    .ai-drawer__conversation-actions { opacity: 1; }
}
</style>
